import React, { useCallback, useEffect, useRef, useState } from 'react';
import Landing from './pages/Landing';
import Home from './pages/Home';
import News from './pages/News';
import People from './pages/People';
import Assistant from './pages/Assistant';
import ProfileEditor from './pages/ProfileEditor';
import PostDetail from './pages/PostDetail';
import Groups from './pages/Groups';
import PublicProfile from './pages/PublicProfile';
import MatchingReview from './pages/MatchingReview';
import AdminDashboard from './pages/AdminDashboard';
import Legal from './pages/Legal';
import CopilotGuide from './pages/CopilotGuide';
import Promote from './pages/Promote';
import NetworkMap from './components/NetworkMap';
import Perk from './pages/Perk';
import { LEGAL_VERSION } from './lib/legal';
import Onboarding from './components/Onboarding';
import Tour, { markTourSeen, tourSeen } from './components/Tour';
import SideNav from './components/SideNav';
import RightRail from './components/RightRail';
import { api, clearSession, getMe, getToken } from './lib/api';
import { setAdminFlag } from './lib/role';

function Toast({ toast }) {
  if (!toast) return null;
  return <div className="toast" role="status" aria-live="polite">{toast}</div>;
}

/* Links in emails open a page: ?go=people&thread=<id>. Kept until after
   sign-in, since the sign-in page clears the address bar. */
(function captureEmailLink() {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get('go')) {
      window.sessionStorage.setItem('bn.go', JSON.stringify({ go: q.get('go'), thread: q.get('thread') || '' }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  } catch { /* private mode */ }
}());

function takeEmailLink() {
  try {
    const saved = window.sessionStorage.getItem('bn.go');
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
}

export default function App() {
  const [me, setMe] = useState(getMe());
  const [view, setView] = useState('home');
  const [tourOpen, setTourOpen] = useState(false);
  const [groupRequest, setGroupRequest] = useState(null);
  // Someone's profile, opened by clicking their name anywhere (lib/nav.js).
  const [profileHandle, setProfileHandle] = useState(null);
  const [profileFrom, setProfileFrom] = useState('home');
  const [counts, setCounts] = useState({});
  const [toast, setToast] = useState('');
  const [openPostId, setOpenPostId] = useState(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  // News is opt-in (My profile → Show News); the code and the matcher's use of it stay.
  const [showNews, setShowNews] = useState(false);
  const [myAvatar, setMyAvatar] = useState('');
  // Home's feed view and the search query live here: the right column sets them.
  const [homeView, setHomeView] = useState('all');
  const [findQuery, setFindQuery] = useState('');
  const [termsCurrent, setTermsCurrent] = useState(true);
  // buildersnetwork.xyz/perks/<slug>: open that perk once signed in.
  const [perkSlug] = useState(() => {
    const m = typeof window !== 'undefined' && window.location.pathname.match(/^\/perks\/([a-z0-9-]+)$/);
    return m ? m[1] : '';
  });
  // Bumped when setup finishes, so everything showing the profile re-reads it.
  const [profileVersion, setProfileVersion] = useState(0);
  // Which profile field to land on when "Add it" opens the editor. A fresh
  // object each time, so asking for the same field twice still scrolls.
  const [focusRequest, setFocusRequest] = useState(null);
  // Bumped after every profile save so the meter keeps up while you type.
  const [railTick, setRailTick] = useState(0);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [copilotFile, setCopilotFile] = useState(null);
  // A conversation Copilot was asked to open ("open my chat with Sabrina").
  const [threadRequest, setThreadRequest] = useState(null);
  const homeRef = useRef(null);
  // Set by the profile editor while it is open: save before leaving, and say so
  // if a save failed rather than dropping it silently.
  const editorGuard = useRef(null);

  const signedIn = Boolean(getToken() && me);

  // The People badge: introductions waiting on you plus messages you haven't
  // read. Re-checked on every page change and once a minute, so a reply shows
  // up without a reload.
  const refreshCounts = useCallback(() => Promise.all([
    api.proposals().catch(() => []),
    api.unread().catch(() => ({ messages: 0 })),
    api.myReferrals().catch(() => []),
  ]).then(([p, u, refs]) => {
    const asks = refs.filter((r) => r.role === 'candidate' && r.status === 'pending_consent').length;
    setCounts((c) => ({ ...c, people: p.length + asks + (u?.messages || 0), groups: u?.groups || 0 }));
  }), []);
  useEffect(() => {
    if (!signedIn) return undefined;
    refreshCounts();
    const t = setInterval(refreshCounts, 60000);
    return () => clearInterval(t);
  }, [signedIn, view, refreshCounts]);

  useEffect(() => {
    if (signedIn && perkSlug) setView('perk');
  }, [signedIn, perkSlug]);

  useEffect(() => {
    if (!signedIn) return;
    const link = takeEmailLink();
    if (!link) return;
    try { window.sessionStorage.removeItem('bn.go'); } catch { /* private mode */ }
    if (link.thread) setThreadRequest({ id: link.thread, at: Date.now() });
    if (['home', 'people', 'groups', 'news', 'profile', 'admin', 'matching', 'promote', 'network'].includes(link.go)) setView(link.go);
  }, [signedIn]);

  useEffect(() => {
    const onProfile = (e) => {
      setProfileHandle(e.detail);
      setView((v) => { if (v !== 'member') setProfileFrom(v); return 'member'; });
      setCopilotOpen(false);
      window.scrollTo(0, 0);
    };
    window.addEventListener('bn:profile', onProfile);
    return () => window.removeEventListener('bn:profile', onProfile);
  }, []);
  useEffect(() => {
    const onFile = (e) => { setCopilotFile({ id: e.detail, at: Date.now() }); setCopilotOpen(true); };
    window.addEventListener('bn:copilot-file', onFile);
    return () => window.removeEventListener('bn:copilot-file', onFile);
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    api.me()
      .then((p) => {
        setNeedsSetup(!p.onboarded);
        // First visit (after setup, if any): the walkthrough, once per device.
        if (p.onboarded && !tourSeen(p.id)) setTimeout(() => setTourOpen(true), 600);
        setIsAdmin(Boolean(p.is_admin));
        setAdminFlag(p.is_admin);
        // An admin link (from an email) opened by a non-admin would be a blank page.
        if (!p.is_admin) setView((v) => (v === 'admin' || v === 'matching' ? 'home' : v));
        setMyAvatar(p.avatar_url || '');
        setShowNews(Boolean(p.show_news));
        setTermsCurrent(p.terms_current !== false);
      })
      .catch(() => {});
  }, [signedIn]);

  useEffect(() => {
    if (!copilotOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setCopilotOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [copilotOpen]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const legalPage = typeof window !== 'undefined' && ({ '/privacy': 'privacy', '/terms': 'terms' })[window.location.pathname];
  if (legalPage) return <Legal which={legalPage} />;
  if (typeof window !== 'undefined' && window.location.pathname === '/guide/copilot') return <CopilotGuide />;

  if (!signedIn) {
    return <Landing onEntered={(s) => setMe({ id: s.member_id, handle: s.handle, name: s.name })} />;
  }

  async function go(next, { focus, thread, post, group, topic } = {}) {
    if (thread) setThreadRequest({ id: thread, at: Date.now() });
    if (next === 'post' && post) { openPost(post); return; }
    if (group) setGroupRequest({ id: group, topic, at: Date.now() });
    // Copilot is a panel now, not a page; asking for it opens the panel.
    if (next === 'assistant' || next === 'copilot') {
      setCopilotOpen(true);
      return;
    }
    if (focus) setFocusRequest({ key: focus, at: Date.now() });
    const guard = editorGuard.current;
    if (view === 'profile' && guard) {
      await guard.flush();
      if (guard.hasUnsaved()
        && !window.confirm("Some of your profile changes didn't save. Leave anyway?")) {
        return;
      }
    }
    setView(next);
  }

  function openPost(id) {
    setOpenPostId(id);
    go('post');
  }

  return (
    <div className="app">
      <SideNav
        view={view}
        setView={go}
        counts={counts}
        me={me ? { ...me, avatar_url: myAvatar } : me}
        onSignOut={() => { clearSession(); setMe(null); }}
        notify={setToast}
        copilotOpen={copilotOpen}
        onCopilot={() => setCopilotOpen((o) => !o)}
        isAdmin={isAdmin}
        onTour={() => { go('home'); setCopilotOpen(false); setTimeout(() => setTourOpen(true), 80); }}
        showNews={showNews}
      />

      <main className="app-main">
        {!termsCurrent && (
          <div className="terms-banner" role="region" aria-label="Terms update">
            <span>
              We've published our <a href="/terms" target="_blank" rel="noreferrer">Terms of Use</a> and{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. Continuing to use B/N means you agree to them.
            </span>
            <button className="btn primary sm"
              onClick={() => api.acceptTerms(LEGAL_VERSION).then(() => setTermsCurrent(true)).catch((e) => setToast(e.message))}>
              OK
            </button>
          </div>
        )}
        {view === 'home' && (
          <Home
            key={profileVersion}
            ref={homeRef}
            notify={setToast}
            view={homeView}
            onViewChange={setHomeView}
            query={findQuery}
            onQuery={setFindQuery}
            onOpenPost={openPost}
            onRequestsChanged={() => setRailTick((t) => t + 1)}
            railTick={railTick}
            onGo={go}
          />
        )}
        {view === 'news' && <News notify={setToast} />}
        {view === 'people' && <People notify={setToast} threadRequest={threadRequest} onOpenPost={openPost} />}
        {view === 'member' && profileHandle && (
          <PublicProfile key={profileHandle} handle={profileHandle} onBack={() => setView(profileFrom || 'home')}
            onMessage={(threadId) => go('people', { thread: threadId })} notify={setToast} />
        )}
        {view === 'groups' && <Groups notify={setToast} onChanged={refreshCounts} openRequest={groupRequest} />}
        {view === 'matching' && isAdmin && <MatchingReview notify={setToast} go={go} />}
        {view === 'admin' && isAdmin && <AdminDashboard notify={setToast} go={go} />}
        {view === 'promote' && <Promote notify={setToast} />}
        {view === 'network' && (
          <div className="page">
            <NetworkMap notify={setToast} />
          </div>
        )}
        {view === 'perk' && <Perk slug={perkSlug} notify={setToast} onGo={go} />}
        {view === 'profile' && <ProfileEditor
            key={profileVersion}
            notify={setToast}
            guardRef={editorGuard}
            focusRequest={focusRequest}
            onSaved={() => setRailTick((t) => t + 1)}
            onShowNewsChanged={setShowNews}
            onAvatarChanged={(u) => { setMyAvatar(u); setRailTick((t) => t + 1); }}
          />}
        {view === 'post' && openPostId && (
          <PostDetail postId={openPostId} notify={setToast} onBack={() => go('home')} />
        )}

      </main>

      <RightRail
        view={view}
        onGo={go}
        refresh={`${profileVersion}:${railTick}`}
        onOpenPost={openPost}
        notify={setToast}
        homeView={homeView}
        onHomeView={setHomeView}
        query={findQuery}
        onQuery={setFindQuery}
        onRequestsChanged={() => homeRef.current?.reload?.()}
      />

      {copilotOpen && (
        <aside className="copilot-panel" aria-label="Copilot">
          <Assistant
            notify={setToast}
            onNavigate={go}
            onClose={() => setCopilotOpen(false)}
            fileRequest={copilotFile}
            onApplied={() => { setProfileVersion((v) => v + 1); setRailTick((t) => t + 1); }}
          />
        </aside>
      )}

      {needsSetup && (
        <Onboarding
          onDone={() => {
            setNeedsSetup(false); setProfileVersion((v) => v + 1);
            if (me?.id && !tourSeen(me.id)) setTimeout(() => setTourOpen(true), 400);
          }}
          onOpenProfile={() => go('profile')}
        />
      )}

      {tourOpen && <Tour onClose={() => { setTourOpen(false); if (me?.id) markTourSeen(me.id); }} />}

      <Toast toast={toast} />
    </div>
  );
}
